import {
	faBaby as farBaby,
	faBabyCarriage as farBabyCarriage,
	faHandHoldingDollar as farHandHoldingDollar,
	faHouse as farHouse,
	faHouseHeart as farHouseHeart,
	faPaintRoller as farPaintRoller,
	faQuestion as farQuestion,
	faShuffle as farShuffle,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCalculator as fasCalculator,
	faFileSignature as fasFileSignature,
	faPaste as fasPaste,
	faPhone as fasPhone,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { MortgageCalculator } from "@component/Mortgage/Calculator/MortgageCalculator";
import type { FAQSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { YoutubeVideo } from "@component/Widget/YoutubeVideo/YoutubeVideo";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import emagImg from "@img/landing/lakashitel/emag-logo.png";
import { BankLogosCloud } from "@landing/components/BankLogosCloud";
import { Barometer } from "@landing/components/Barometer";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { ExpertSlider } from "@ui/components/ExpertSlider";
import { InfoCard } from "@ui/components/InfoCard";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { BmBadge, FeatureBox, LandingContainer, LandingHowTo, LandingHowToItem, List } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Accordion, Card, Col, Container, Row, Tab, Tabs } from "react-bootstrap";

/**
 * @Routing
 *
 * @title "Lakáshitel kalkulátor [YEAR]"
 * @description "Tekintsd át a lakáshitelek széleskörű kínálatát 2024-ben! A megfelelő lakáshitel kiválasztása után, segítünk a hitel igénylés teljes folyamatában."
 * @route "/lakashitel-kalkulator/"
 */
export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<MortgageCalculator
					title={
						<>
							<span className="d-md-none display-4 font-weight-black">Lakáshitel kalkulátor</span>
							<span className="d-none d-md-inline">Lakáshitel kalkulátor</span>
						</>
					}
					badge={
						<BmBadge
							prependAdditionalClasses="bg-white"
							className="ml-xl-5"
							size="lg"
							prepend={<img src={emagImg} className="badge-img" alt="eMAG logo" />}
						>
							az online előminősítés most 50 ezer ft eMAG utalványt érhet.
						</BmBadge>
					}
					description={
						<>
							<span className="d-md-none">Spórolj milliókat!</span>
							<span className="d-none d-md-inline">
								Spórolj milliókat! Egyedi kamatkedvezmény a Bankmonitor ügyfeleinek!
							</span>
						</>
					}
				/>
				<LandingContainer>
					<Barometer />
				</LandingContainer>
				<LandingContainer>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Szakértőink, akik Neked segítenek</h2>
					<ExpertSlider groupName="BM_HQ_MORTGAGE" />
				</LandingContainer>
				<LandingContainer size="lg" bgClassName="bg-gray">
					<LandingHowTo title="Így kaphatod meg a lakáshitelt">
						<LandingHowToItem title="Kalkuláció" icon={<FontAwesomeIcon icon={fasCalculator} />}>
							Versenyeztesd meg a bankok ajánlatait, és válaszd ki a számodra legkedvezőbbet.
						</LandingHowToItem>
						<LandingHowToItem title="Érdeklődés" icon={<FontAwesomeIcon icon={fasPhone} />}>
							Add le érdeklődésed a választott lakáshitelre, és szakértőnk visszahív.
						</LandingHowToItem>
						<LandingHowToItem title="Ügyintézés" icon={<FontAwesomeIcon icon={fasPaste} />}>
							Ha kiválasztottad a számodra megfelelő bankot, segítünk az ügyintézésben is.
						</LandingHowToItem>
						<LandingHowToItem title="Megkapod a pénzt" icon={<FontAwesomeIcon icon={fasFileSignature} />}>
							A hitelbírálati folyamatot és a szerződéskötést követően megkapod a pénzt.
						</LandingHowToItem>
					</LandingHowTo>
				</LandingContainer>
				<LandingContainer>
					<BankLogosCloud
						variant="mortgage"
						title="Lakáshitel kalkulátorunk az alábbi bankok termékeit tartalmazza"
					/>
				</LandingContainer>
				<LandingContainer>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Amit tudni érdemes a lakáshitelről</h2>
					<Tabs
						id="how-to"
						className="mb-3 nav-secondary"
						defaultActiveKey="feltetelek"
						onSelect={(k) =>
							GoogleAnalytics4.sendEvent("BM_blockToggle", {
								tab: k,
							})
						}
					>
						<Tab eventKey="feltetelek" title="Feltételek">
							<Accordion className="accordion-secondary">
								<LandingAccordion
									title="Mennyi hitelt kaphatok az adott ingatlanra?"
									parentName="feltetelek"
								>
									<p>
										A felvehető lakáshitel szempontjából fontos a fedezetül szolgáló ingatlan{" "}
										<a href="/jobb-tudni/lakashitel/?link_type=landing-faq#ingatlan-forgalmi-erteke-vagy-piaci-erteke">
											becsült forgalmi értéke
										</a>
										, ugyanis a hitelösszeg nem haladhatja meg ennek 80%-át. Azonban ez is csak a
										jogszabályi plafon, amitől a bankok elérhetnek. Általánosságban elmondható, hogy
										Budapesten és a nagyobb városokban ez az arány magasabb, míg a kisebb
										településeken, falvakban, ahol a kevés tranzakció miatt nehezen becsülhető meg
										pontosan egy ingatlan értéke, alacsonyabb.
									</p>
									<p>
										A „
										<a href="/lakashitel-kalkulator/mennyi-lakashitelt-kaphatok/?link_type=landing-faq">
											Mennyi lakáshitelt kaphatok?
										</a>
										” kalkulátorral bárki számszerűsítheti, hogy az igazolt jövedelem, a meglévő
										hiteltörlesztések és egyéb fontos szempontok alapján mekkora összegű lakáshitelt
										vehet fel!
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mekkora minimális jövedelem szükséges egy lakáshitelhez?"
									parentName="feltetelek"
								>
									<p>
										A bankok minden esetben meghatározzák a minimálisan elvárt jövedelem nagyságát,
										ami általában a minimálbér, vagy ezt meghaladó összeg. Sokan nem tudják, hogy
										vannak úgynevezett elsődleges illetve másodlagos jövedelmek. A bankok elvárják,
										hogy az elsődleges jövedelemből összejöjjön a minimálisan elvárt összeg, ami ha
										teljesül, akkor a másodlagos jövedelmet is figyelembe veszik a bevállalható havi
										törlesztő meghatározásánál.
									</p>
									<p>
										<i>Elsődleges jövedelmek:</i> munkabér, vállalkozói jövedelem, öregségi nyugdíj,
										végleges rokkantsági nyugdíj. A KATA-s vállalkozó esetében a NAV által
										kiállított jövedelemigazoláson az éves kiszámlázott összeg 60%-a szerepel.
									</p>
									<p>
										<i>Másodlagos jövedelem:</i> GYES, GYED, családi pótlék, bónusz stb.
									</p>
									<h4>Hogyan fogadják el lakáshitelnél a kismama jövedelmét?</h4>
									<p>
										A gyermekvállalás és a lakásvásárlás, ezzel pedig a hitelfelvétel sok esetben
										összekapcsolódik. Vagy azért, mert az első gyermek megszületésének idejére a
										család már szeretne saját otthonban élni, vagy azért, mert az új jövevény miatt
										a meglévő lakás már kicsi lesz, ezért váltanának nagyobbra.
									</p>
									<p>
										1. Van bank, ahol a{" "}
										<strong>várandós nő aktuális nettó jövedelmét veszik alapul</strong>, ám azzal a
										kikötéssel, hogy a munkáltatónak nyilatkoznia kell, mikortól várja vissza az
										anyukát, valamint az adósnak vállalnia kell, hogy az adott időponttól munkába
										fog állni.
									</p>
									<p>
										2. Van bank, ahol az aktuális nettó jövedelmet{" "}
										<strong>
											csökkentett mértékben, például 70%-os aránnyal veszik csak számításba.
										</strong>
									</p>
									<p>
										3. Olyan bankkal is találkozhatunk, ahol ilyenkor a{" "}
										<strong>GYES összegével számolnak</strong> csak. Ez azt jelenti, hogy a másik
										adósnak legalább minimálbért kell tudnia igazolni, ami ha teljesül, akkor ehhez
										adják hozzá a GYES összegét, így jön ki a havi nettó jövedelem összege.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mennyi lakáshitelt kaphatok a jövedelmem alapján?"
									parentName="feltetelek"
								>
									<p>
										Az igazolt jövedelmed nagyságától és a{" "}
										<a href="/raiffeisen-bank-szakertoi-cikksorozat/milyen-ingatlanokat-fogadnak-el-hitelfedezetkent-a-bankok/?link_type=landing-faq">
											fedezetként bevont ingatlan
										</a>{" "}
										becsült forgalmi értékétől függ, hogy mekkora összegű lakáshitelt kaphatsz.
										Jövedelmi oldalról a JTM (
										<a href="/jobb-tudni/babavaro/?link_type=landing-faq#jovedelemaranyos-torlesztoreszlet-mutato-jtm">
											jövedelemarányos törlesztőrészlet-mutató
										</a>
										) határozza meg a hivatalos plafont.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mennyi ideje kell az adott munkahelyen dolgoznom?"
									parentName="feltetelek"
								>
									<p>
										Alkalmazotti jövedelem esetén minimális elvárás a legalább 3 hónapos
										munkaviszony az aktuális munkahelyen. Legjobb a határozatlan idejű munkavállalói
										szerződés, de a határozott idejű sem jelent feltétlenül akadályt, ám ezeket az
										egyes bankok különbözőképpen kezelik, illetve sokuknál egyedileg bírálják el az
										ilyen igényléseket. A 3 hónap szempontjából lényeges, hogy ez három teljes
										hónapot jelent, vagyis aki például január 15-én kezdett, az legkorábban május
										1-től igényelhet lakáskölcsönt.
									</p>
									<h4>Nem állhatsz már próbaidő alatt</h4>
									<p>
										Amennyiben hitelt igényelnél, akkor nem elég, hogy már legalább 3 hónapja az
										adott munkahelyen dolgozz, az is feltétel, hogy a próbaidőd az igénylés idejére
										lejárjon, hogy véglegesítsenek. A bankok részéről érthető ez az elvárás, hiszen
										próbaidő alatt a munkavállaló és a munkáltató egyaránt indoklási kötelezettség
										nélkül dönthet a munkakapcsolat megszüntetéséről.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mennyi idő alatt kapom meg a lakáshitelt?"
									parentName="feltetelek"
								>
									<p>
										Az átlagos elbírálási idő <strong>1 – 1,5 hónap</strong>. Ez hosszabb lehet,
										amennyiben a hitelbírálat alatt kiderül valamilyen új információ (például egy
										eltitkolt hitelkártya), mivel a bank ilyenkor új papírokat kérhet be. Továbbá
										több időt vehet igénybe a hitelügyintézés akkor is, ha például{" "}
										<a href="/csok-feltetelei/?link_type=landing-faq">CSOK támogatást</a> kérsz a
										hitelhez.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Milyen dokumentumokra van szükség a lakáshitel igényléséhez?"
									parentName="feltetelek"
								>
									<ul>
										<li>Személyazonosító igazolvány vagy érvényes úti okmány</li>
										<li>Adóigazolvány</li>
										<li>Lakcímet igazoló hatósági igazolvány</li>
										<li>Vállalkozói igazolvány (vállalkozók esetében)</li>
										<li>Munkáltatói igazolás (banki formanyomtatvány)</li>
										<li>
											NYUFIG igazolás (adott évről szóló), valamint a befogadás dátumát megelőző
											utolsó 2 havi nyugdíjszelvény (nyugdíjasok esetében)
										</li>
										<li>NAV jövedelemigazolás (vállalkozó esetében)</li>
										<li>
											NAV jövedelemigazolás (alkalmazottak esetében):
											<ul>
												<li>ha az ügyfél készpénzben kapja jövedelmét</li>
												<li>ha a munkáltató egyéni vállalkozó</li>
												<li>
													ha az ügyfél tulajdonos abban a cégben, amely a munkáltatói
													igazolást kiállította
												</li>
												<li>
													ha a munkáltatói igazolás kiállítója az ügyfél hozzátartozója vagy
													maga az ügyfél
												</li>
											</ul>
										</li>
										<li>
											NAV nemleges igazolás köztartozásról (saját vállalkozásból származó
											jövedelem esetén)
										</li>
										<li>
											Más banknál vezetett számla esetén a befogadás dátumát megelőző utolsó 3
											havi, eredeti vagy hitelesített számlakivonat
										</li>
										<li>
											A fedezetként szolgáló ingatlan(ok) 30 napnál nem régebbi tulajdoni lapja(i)
										</li>
										<li>Kitöltött hitelkérelmi adatlap</li>
									</ul>
								</LandingAccordion>
								<LandingAccordion
									title="Milyen futamidejű lakáshitelt válasszak?"
									parentName="feltetelek"
								>
									<p>
										A lakáshitelek maximális futamideje lehet akár 35 év is, azonban sokszor
										felesleges ilyen hosszú időre elhúzni a törlesztést. Tudnod kell, hogy{" "}
										<strong>
											minél hosszabb a futamidő, annál magasabb a teljes visszafizetés a bank
											felé.
										</strong>{" "}
										Inkább abból indulj ki, hogy mekkora törlesztőrészletet tudsz vállalni, mivel ez
										a lehetséges futamidőt is befolyásolja. A törlesztőrészlet ne annyi legyen,
										amennyit éppen ki tudsz fizetni! Maradjon tartalék is arra az esetre, ha
										emelkedne a kamat a futamidő alatt!
									</p>
									<p>
										<strong>TIPP:</strong> a lakáshitel kalkulátorban nézd meg a szükséges
										hitelösszeghez tartozó havi törlesztőket és teljes visszafizetéseket különböző
										futamidők esetén!
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mikor változhat egy lakáshitel törlesztőrészlete?"
									parentName="feltetelek"
								>
									<p>
										Egy lakáshitel törlesztőrészlete a futamidő alatt többször módosulhat,
										emelkedhet vagy csökkenhet. Hogy hányszor változhat, az attól függ, milyen
										hosszú{" "}
										<a href="/jobb-tudni/lakashitel/?link_type=landing-faq#kamatperiodus">
											kamatperiódust
										</a>{" "}
										választasz. A kamatperiódus az az időszak, ami alatt a hitel kamata változatlan.
										Az új lakáshitelek többsége 5 vagy 10 éves kamatperiódusú, de népszerűek a{" "}
										<strong>futamidő végéig rögzített kamatú kölcsönök</strong> is. A forintosított
										devizahitelesek közül azonban sokaknak van 3 havonta változó kamatú hitele,
										amelyek jelentős mértékben ki vannak téve egy esetleges kamatemelkedés
										következményeinek.
									</p>
									<p>A kamatperiódussal kapcsolatban két általános szabály van:</p>
									<ul>
										<li>Minél hosszabb egy hitel kamatperiódusa, annál kisebb a kamatkockázat.</li>
										<li>
											Minél hosszabb egy hitel kamatperiódusa, annál magasabb a kamata. Azonban
											előfordulhat, hogy az egyik bank olcsóbban adja a maga 10 éves
											kamatperiódusú hitelét, mint amennyiért egy másik banknál 5 évre rögzített
											kamatú kölcsönt kaphatsz. Éppen ezért érdemes olyan összehasonlító
											hitelkalkulátort használnod, amivel együtt láthatók az ajánlatok.
										</li>
									</ul>
								</LandingAccordion>
								<LandingAccordion
									title="Mekkora önerővel kaphatok lakáshitelt?"
									parentName="feltetelek"
								>
									<p>
										Általánosan elmondható, hogy egy lakáshitel esetében 20% az elvárt minimális
										önerő nagysága, vagyis egy 20 millió Ft-os ingatlan megvásárlásához legalább 4
										millió Ft saját forrás bevonása szükséges.
									</p>
									<h4>Vehetek fel lakáshitelt önerő nélkül?</h4>
									<p>
										Megoldható, hogy önerő nélkül vegyél fel lakáshitelt, ám ebben az esetben egy
										másik ingatlan bevonására van szükség pótfedezetként. A hitelösszeg ebben az
										esetben a fedezetként bevont ingatlanok becsült forgalmi értékének legfeljebb
										80%-a lehet, de a bankok ettől lefelé eltérhetnek és el is térnek.
									</p>
									<h4>CSOK és Babaváró felhasználása önerőként egy lakáshitelhez</h4>
									<p>
										Egy kisebb értékű ingatlan megvásárlása esetén az önerőt pont fedezheti a
										gyerekek után igénybe vehető CSOK összege. Ezt meglévő gyermekek esetén minden
										bank elfogadja önerőként, ám vállalt gyermeknél már ez nem egyértelmű.{" "}
										<a href="/mediatar/cikk/kulonbozokeppen-fogadjak-el-onerokent-a-csok-ot-de-a-babavaro-is-okozhat-meglepeteseket-egy-lakashitelnel/?link_type=landing-faq">
											A CSOK önerőként történő felhasználhatóságáról itt olvashatsz részletesen.
										</a>
									</p>
									<p>
										A <a href="/babavaro-hitel/?link_type=landing-faq">Babaváró támogatás</a> is
										segíthet az önerő előteremtésében. Ha lakásvásárlási célra igényeljük a
										konstrukciót, akkor jogszabály alapján a kölcsön 25%-át ingatlantehernek, míg
										75%-át önerőnek lehet tekinteni. Például egy 20 millió forintos lakás
										megvásárlására a vevő felvenne 10 millió forint Babaváró hitelt és 10 millió
										forint lakáshitelt. A bank a vételárnak maximum a 70 százalékáig hitelez, vagyis
										14 millió forint hitelt ad. Esetünkben a lakáshitel, valamint a Babaváró hitel
										25%-a számít ingatlant terhelő hitelnek, ezek együttes nagysága pedig 12,5
										millió forint. Vagyis ezek alapján a vásárlás megfinanszírozható fedezeti
										oldalról tényleges saját megtakarítás nélkül is.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Milyen ingatlant fogadnak el fedezetként a bankok?"
									parentName="feltetelek"
								>
									<p>
										Fedezetként csak Magyarország területén lévő,{" "}
										<strong>
											magánszemély tulajdonában levő per-, teher- és igénymentes, önállóan
											forgalomképes
										</strong>{" "}
										ingatlan szolgálhat, melynek műszaki állapota végleges és tulajdonviszonyai
										rendezettek.
									</p>
									<p>
										Nagyon fontos, hogy a hitel mögé felajánlott ingatlannak lakhatásra alkalmasnak
										kell lennie. Nem kell mérnöknek lenni ahhoz, hogy meg tudja valaki állapítani
										egy lakásról, hogy lakható-e, vagy sem. Van működő fürdő és WC? Van konyha és
										abban vízvételi lehetőség? Van ablak a szobán? Van állandó fűtés? Ha ezek
										valamelyike nincs, akkor az ingatlan lakhatatlannak számít.
									</p>
									<p>
										Ekkor sincs azonban minden veszve, hiszen előfordulhat, hogy jó áron vásárolna
										meg valaki egy lerobbant ingatlant, amit azután hitelből újítana fel. Megoldást
										jelenthet ebben az esetben, ha például egy családon belüli (fedezetként
										bevonható) ingatlanra vesz fel hitelt valaki, így finanszírozva meg a
										munkálatokat.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Jelzáloghitelt vagy személyi kölcsönt vegyek fel?"
									parentName="feltetelek"
								>
									<p>
										A{" "}
										<a href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/?link_type=landing-faq">
											szabad felhasználású jelzáloghitelek
										</a>{" "}
										tetszőleges célra igényelhetők, így ezek gyakran a személyi kölcsönök
										alternatívái. A szabad felhasználású jelzáloghitelek előnye a személyi
										kölcsönökhöz képest, hogy itt nagyobb a maximális hitelösszeg és futamidő. (
										<a href="/szemelyi-kolcson/?link_type=landing-faq">Személyi kölcsönnél</a> a
										hitelösszeg legfeljebb 10 millió Ft, a futamidő pedig 10 év lehet.)
									</p>
									<p>
										A szabad felhasználású jelzáloghitelek kamata is kedvezőbb a személyi
										kölcsönökénél, ami annak köszönhető, hogy a jelzáloghiteleknél van
										ingatlanfedezet, ami nagyobb biztonságot jelent a bank számára.
									</p>
									<p>
										Kisebb – jellemzően 1-5 millió forintos – hitelösszeg esetén mégis kedvezőbb
										lehet egy személyi kölcsön, ugyanis itt lényegesen alacsonyabbak az induló,
										egyszeri költségek. (Például nincs{" "}
										<a href="/jobb-tudni/lakashitel/?link_type=landing-faq#ertekbecsles">
											értékbecslés
										</a>
										,{" "}
										<a href="/jobb-tudni/lakashitel/?link_type=landing-faq#kozjegyzoi-okirat-tartozaselismero-okirat">
											közjegyzői és ügyvédi díj
										</a>
										, mint ahogy földhivatali bejegyzésre sincs szükség.)
									</p>
									<p>
										A személyi kölcsönök további előnye, hogy ezeknél nincs szükség
										ingatlanfedezetre, valamint az igénylést követően átlagosan 5-7 nap alatt
										megtörténhet a folyósítás, míg a lakáshiteleknél{" "}
										<strong>az átfutási idő 1-2 hónap is lehet.</strong>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Vehetek fel lakáshitel egy másik hitel kiváltására?"
									parentName="feltetelek"
								>
									<p>
										Jelzáloghitel igényelhető meglévő hitel vagy hitelek (akár egy személyi kölcsön,
										hitelkártya hitel, áruhitel){" "}
										<a href="/hitelkivaltas-reszletek/?link_type=landing-faq">kiváltására</a> is,
										mert a lakáshitel kamata ezeknél alacsonyabb, így összegében alacsonyabb
										törlesztő érhető el. A meglévő lakáshitel is kiváltható másikkal, vagy azért,
										mert kedvezőbb kamatszint érhető el, vagy azért, mert egy kiváltással hosszabb
										kamatperiódusra, ezzel pedig kamatkockázat szempontjából kiszámíthatóbb
										kölcsönre lehet váltani.
									</p>
									<p>
										Ez utóbbi különösen a{" "}
										<strong>változó kamatozású lakáshitelek esetében lehet előnyös</strong>, hiszen
										ezek kamata 3-12 havonta változhat, így egy esetleges kamatemelkedés hatása
										nagyon gyorsan begyűrűzhet a havi törlesztőbe is. Bár az új lakáshitelek között
										a változó kamatozásúak aránya elenyésző, a teljes hitelállományon belül mégis
										több százezer ilyen szerződés él, mert a devizahiteleket anno 3 havonta változó
										kamatú forinthitelekre „alakították” át.
									</p>
									<p>
										Hitelkiváltás célú hitelfelvételnél fontos előny, hogy ilyenkor a kiváltandó
										hitel törlesztőjét nem veszik figyelembe a JTM-vizsgálatnál, hiszen a kiváltást
										követően ezt már nem kell fizetni.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Hogyan kaphat lakáshitelt egy KATA-s adózó?"
									parentName="feltetelek"
								>
									<p>
										A KATA-s jövedelmet általában elfogadják a bankok, ám, hogy mekkora nettó
										jövedelmet állapítanak meg, az már eltérő. Erre nézve három banki gyakorlattal
										találkozhatunk, és ahogy látható, az igénylő szempontjából nagyon nem mindegy,
										melyiket alkalmazzák vele kapcsolatban.
									</p>
									<p>
										1. A{" "}
										<a href="/mediatar/cikk/megoldasok-ha-nav-jovedelemigazolasra-van-szukseged/?link_type=landing-faq">
											NAV jövedelemigazoláson
										</a>{" "}
										lévő összeget (a számlaérték 60%-a) elosztják 12-vel, és ezt tekintik nettó
										jövedelemnek.
									</p>
									<p>
										2. A NAV jövedelemigazoláson található összegből levonják az adóbevallás
										szerinti KATA-adót (havi 25, 50 vagy 75 ezer Ft), majd ezt osztják el 12-vel.
									</p>
									<p>
										3. A NAV jövedelemigazoláson található összegből levonják a KATA-adót, majd ezt
										tovább csökkentik egy a bank által meghatározott képlet alapján.
									</p>
									<p>
										A KATA-sok szempontjából más különbség is van azonban a banki gyakorlatok
										között. Van pénzintézet, amelyik legalább egy teljes (12 hónapos) lezárt
										pénzügyi évet vár el, míg másnál elegendő egy lezárt, nem teljes pénzügyi év is.
										Ez utóbbinál azonban az éves jövedelmet mindenképpen 12-vel osztják el, vagyis a
										havi nettó itt alacsonyabb lesz.
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Kaphatnak lakáshitelt a KHR listán (BAR listán) szereplők?"
									parentName="feltetelek"
								>
									<p>
										A BISZ Zrt. honlapja szerint 2019 végén 1,2 millió fennálló és 263 ezer megszűnt
										mulasztást tartottak nyilván Magyarországon. Őket – a bankkártyás csalásban
										részt vevőkkel és a csalás miatt elutasított lakáshitel-igénylést benyújtókkal
										együtt – az úgynevezett KHR rendszerben (korábbi, máig népszerű nevén, a
										BAR-listán) tartják nyilván. A KHR adatbázis a magyar hitelezésben rendkívül
										fontos szerepet játszik, ugyanis a bankok egy hiteligény elbírálásakor kötelesek
										ellenőrizni, hogy az igénylő fennálló vagy úgynevezett megszűnt mulasztással
										szerepel-e benne. A fennálló tartozás azt jelenti, hogy az adós a lekérdezés
										pillanatában is késedelemben van, míg megszűnt státuszba azok kerülnek, akik
										rendezték a tartozásukat, ám azóta nem telt el legalább egy év. Az előbbiek
										úgynevezett aktív KHR-esek, akik nem kaphatnak a bankoktól lakáshitelt, Babaváró
										hitelt vagy személyi kölcsönt!
									</p>
									<p>
										Azok, akik már rendezték hiteltartozásukat, ám azóta nem telt el legalább egy
										év, úgynevezett passzív KHR státuszúak lesznek. Személyi kölcsönt ők nem
										kaphatnak, ám lakáshitelhez jutni van esélyük, ugyanis a Bankmonitor.hu által
										vizsgált 13 bank közül néhány hitelezi őket. Ezért számukra rendkívül fontos,
										hogy jó helyre nyújtsák be a hiteligénylésüket, amiben a Bankmonitor
										hitelszakértői tudnak segíteni.
									</p>
								</LandingAccordion>
							</Accordion>
						</Tab>
						<Tab eventKey="hitelcelok" title="Hitelcélok">
							<Accordion>
								<LandingAccordion title="Használt lakás vagy ház vásárlása" parentName="hitelcelok">
									<p>
										Lakáshitelt legtöbben használt lakás vagy családi ház megvásárlására vesznek
										fel, az igényelhető hitelösszeget ebben az esetben alapvetően meghatározza az
										adott ingatlan helye és minősége. Budapesten és a nagyobb városokban az ingatlan
										becsült forgalmi értékének maximum 80%-a lehet a felvehető hitel összege, míg a
										kisebb településeken 50-70%-kal érdemes kalkulálni. A pontos arány azonban
										minden esetben a bank egyedi döntésén múlik! Használt ingatlan hitellel történő
										megvásárlásakor figyelni kell arra, hogy az ingatlan lakható állapotú legyen!
									</p>
								</LandingAccordion>
								<LandingAccordion title="Újépítésű ingatlan vásárlása" parentName="hitelcelok">
									<p>
										Aki épülő – akár még csak tervezőasztalon meglévő – újépítésű ingatlant
										vásárolna hitelből, az azzal szembesül, hogy{" "}
										<a href="/mediatar/cikk/milliokat-sporolhatsz-utolag-ha-ujepitesu-lakast-vettel/?link_type=landing-faq">
											nem válogathat óriási választékból
										</a>
										. Az ingatlanfejlesztők ugyanis szinte kizárólag úgy adják el a lakásaikat, ha a
										vevő olyan bankot választ, amelyik nem vár az albetétesítésig, hanem a
										használati engedély megadását követően utal. Igen ám, de a bankok között alig
										van ilyen. Azt pedig tudnod kell, hogy a használatbavételi engedély és az
										albetétesítés között akár hónapok is eltelhetnek.
									</p>
									<p>
										Előfordulhat az is, hogy azért nem tudsz szabadon bankot választani, mert az
										ingatlan kizárólag a projektet magát is finanszírozó banknál hitelezhető. Az
										előbbiek miatt a vevőnek sokszor nincs más lehetősége, mint az adott banktól
										felvenni a lakáshitelt, még akkor is, ha ez a bank nem feltétlenül a
										legkedvezőbb árajánlatot adja éppen a piacon.
									</p>
								</LandingAccordion>
								<LandingAccordion title="Építkezésre" parentName="hitelcelok">
									<p>
										Ha családi házat építenél, vagy másokkal összefogva társasházat építenétek,
										akkor is van lehetőség jelzáloghitelből finanszírozni a munkálatokat.
										Építkezésnél plusz dokumentumokat is be kell majd szerezned: kisebb ingatlannál
										elegendő az egyszerűsített bejelentés, míg nagyobb ház esetén építési engedély
										beszerzése szükséges, de elektronikus naplót és költségvetést is kér majd a
										bank. (Ez utóbbit ráadásul a bank saját formátumában kell benyújtani.) A
										benyújtott dokumentumok alapján a bank elbírálja a kölcsönkérelmet, ha pedig
										pozitív döntés születik, akkor megköthetitek a kölcsönszerződést.
									</p>
									<p>
										Építkezésnél számítanod kell arra, hogy a bank a folyósítás feltételéül
										szabhatja az építési és szerelési vagyonbiztosítás megkötését!
									</p>
									<p>
										Első lépésben az építkezés során az általad biztosított önerőt kell
										felhasználni, a bank pedig csak ezt követően kezd részletekben folyósítani,
										összhangban az építési munkálatok előrehaladtával. Minden folyósítás előtt
										helyszíni szemlét végeznek, amikor a bank szakembere megállapítja az aktuális
										készültségi fokot. A már folyósított összeg után törlesztőrészletet, a még nem
										folyósított rész után pedig úgynevezett rendelkezésre tartási díjat kell
										fizetned.
									</p>
									<p>
										Amennyiben a lakáshitel mellett állami támogatást is igénybe veszel, akkor
										további elvárás, hogy az építési költségek legalább 70%-át számlákkal tudd
										igazolni.
									</p>
									<p>
										<a href="/epitesi-hitel/?link_type=landing-faq">Építési hitel kalkulátor</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Lakásfelújításra, korszerűsítésre vagy bővítésre"
									parentName="hitelcelok"
								>
									<p>
										Felújítási célra is igényelhető jelzáloghitel, ebben az esetben a hitel fedezete
										maga a felújítandó ingatlan. Felújítási hitelnél az elvégzett munkákat utólag
										számlákkal kell igazolni. Vannak bankok, amelyek nem kérnek számlákat, de a
										munkálatok befejezését követően kimegy az értékbecslő és ellenőrzi, hogy tényleg
										megtörtént a felújítás, korszerűsítés. (Támogatott lakáshitel esetén minden
										esetben szükség lesz a számlák benyújtására a bank részére.)
									</p>
									<p>
										<a href="/hitel-lakasfelujitasra/?link_type=landing-faq">
											Felújítási célú lakáshitel kalkulátor
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="Hitelkiváltásra" parentName="hitelcelok">
									<p>
										Jelzáloghitel hitelkiváltásra is igényelhető, ebben az esetben a korábbi –
										kiváltott – kölcsönök megszűnnek és kizárólag az új hitelt kell törlesztened.
										Amennyiben hitelkiváltási céllal igényelsz kölcsönt, akkor a bank a lecserélendő
										hitelek törlesztőivel természetesen már nem számol a{" "}
										<a href="/mediatar/cikk/valtozik-a-jtm-tobb-jovedelem-kell-majd-ugyanakkora-hitelhez/?link_type=landing-faq">
											JTM-vizsgálat
										</a>{" "}
										során. A lakáshitellel történő hitelkiváltás jelentős pénzügyi előnyt jelenthet
										akkor, ha valakinek több drága hitele is van, mert egy jelzáloghitel jelenleg
										kedvező kamatokkal érhető el akár a futamidő végéig fix kamat mellett is. A
										hitelkiváltás azoknak is megfontolandó lehet, akik jelenleg változók kamatú
										hitellel rendelkeznek, hiszen ezek kamata és havi törlesztői 1-12 havonta
										módosulhatnak, emiatt például 10 éves kamatperiódusú kölcsönre váltás jelentősen
										mérsékelheti a kamatkockázatot.
									</p>
								</LandingAccordion>
								<LandingAccordion title="Szabad felhasználásra" parentName="hitelcelok">
									<p>
										Ingatlanfedezetű hitelt akár szabad felhasználásra is igényelhetsz, ebben az
										esetben a pénzt bármire – autó vagy nyaraló megvásárlására, felújításra stb. –
										fordíthatod, ám ezt a bank felé nem kell igazolnod.
									</p>
									<p>
										<a href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/?link_type=landing-faq">
											Szabad felhasználású lakáshitel kalkulátor
										</a>
									</p>
								</LandingAccordion>
							</Accordion>
						</Tab>
						<Tab eventKey="tamogatasok" title="Támogatások">
							<Accordion>
								<LandingAccordion
									title="CSOK (családi otthonteremtési kedvezmény)"
									parentName="tamogatasok"
								>
									<p>
										A <a href="/csok-feltetelei/?link_type=landing-faq">csok támogatás</a> a
										családok lakáshoz jutását segítő vissza nem térítendő támogatás. Összege függ a
										gyermekek számától – ez lehet meglévő és vállalt is -, valamint a lakáscéltól.
									</p>
									<p>Csok támogatás jelenleg az alábbi célra lehet igényelni:</p>
									<ul>
										<li>Új lakás építésére</li>
										<li>Új építésű lakás vásárlására</li>
										<li>Használt lakás vásárlására</li>
										<li>Lakásbővítésre</li>
									</ul>
									<p>
										Emellett falusi csok-ot fel lehet igényelni az úgynevezett preferált
										kistelepüléseken megvalósuló:
									</p>
									<ul>
										<li>Lakáskorszerűsítésre</li>
										<li>Lakásbővítésre</li>
										<li>Lakáskorszerűsítésre és bővítésre</li>
										<li>
											Használt lakás vásárlására, amennyiben ezzel egy időben korszerűsítik
											és/vagy bővítik az ingatlant
										</li>
									</ul>
									<p>
										Ezek mellé érkezett 2021. januártól egy újabb cél: már a{" "}
										<strong>
											tetőtér-beépítéssel létrejövő többgenerációs családi ház is
											megfinanszírozható
										</strong>{" "}
										csok támogatásból.
									</p>
									<h4>Illetékmentesség csok-kal történő vásárlás esetén</h4>
									<p>
										Amennyiben valaki csok támogatás igénybevételével vásárol újépítésű vagy
										használt ingatlant, akkor az alapesetben 4%-os vagyonszerzési illetéket nem kell
										megfizetnie.
									</p>
									<p>
										<a href="/csok-feltetelei/?link_type=landing-faq">Tovább a csok támogatásra</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="CSOK hitel" parentName="tamogatasok">
									<h4>Mit kell tudni a csok (családi otthonteremtési kedvezmény) lakáshitelről?</h4>
									<p>A csok hitellel kapcsolatban 2019-ben két fontos változás történt:</p>
									<ul>
										<li>
											Már <strong>használt lakás vásárlására is igényelhető</strong> (míg korábban
											kizárólag újépítésű ingatlan vásárlására és építésére lehetett
											felhasználni).
										</li>
										<li>
											A felvehető maximális hitelösszeg{" "}
											<strong>3 gyermek megléte vagy vállalása esetén 15 millió Ft</strong>, 2
											gyermekkel pedig 10 millió Ft a felső határ.
										</li>
									</ul>
									<h4>
										Milyen összegű csok hitelt igényelhetek használt és újépítésű ingatlan
										vásárlására?
									</h4>
									<p>
										A felvehető maximális hitelösszeg – függetlenül attól, hogy új vagy használt
										ingatlan vásárlására fordítanánk – 3 gyermek megléte vagy vállalása esetén 15
										millió Ft, míg <strong>2 gyermeknél 10 millió Ft</strong>.
									</p>
									<p>
										<a href="/csok/?link_type=landing-faq">CSOK hitel kalkulátor</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="Babaváró hitel" parentName="tamogatasok">
									<p>
										A Babaváró támogatást – más néven a Babaváró hitelt – 2019. július 1-től
										igényelhetik a feltételeknek megfelelő fiatal párok. A Babaváró maximális
										összege 10 millió forint, futamideje legfeljebb 20 év lehet, és{" "}
										<a href="/mediatar/cikk/mekkora-onero-kell-babavaro-hitellakashitel-kombinacio-eseten/?link_type=landing-faq">
											meghatározott feltételekkel önerőként is felhasználható egy lakáshitelhez
										</a>
										.
									</p>
									<p>
										A Babaváró az első 5 évben mindenképp kamatmentes, mennyiben pedig ezen idő
										alatt gyermek születik, akkor a hitel a futamidő végéig kamatmentes marad, a
										havi törlesztést pedig 3 évre fel lehet függeszteni. Ez éppen akkor csökkentheti
										a család terheit, amikor a kismama alacsonyabb jövedelemre számíthat. A második
										gyermek születését – pontosabban 12. hetes magzati korát – követően újabb három
										évre felfüggeszthető a törlesztés, valamint elengedik a fennálló tőketartozás
										30%-át. A 3. gyermeknél a fennálló tartozást teljes egészében elengedik, vagyis
										megszűnik a hitel.
									</p>
									<p>
										<a href="/babavaro-hitel/?link_type=landing-faq">Tovább a Babaváró hitelre</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="Áfa-visszaigénylés" parentName="tamogatasok">
									<p>
										A családok lakáscéljának megvalósításához jelentős segítséget nyújthat az
										utólagos áfa-visszatérítés:
									</p>
									<ul>
										<li>
											Az 5%-os áfával kiállított számlák adótartalma visszaigényelhető újépítésű
											lakás vásárlása, illetve generálkivitelezővel történő építkezés esetén
											akkor, ha csok támogatást is kér a család ezekhez.
										</li>
										<li>
											27%-os adótartalmú számlákat önerős építkezéseknél kap az építtető. Az áfa
											ebben az esetben is visszaigényelhető 5 millió forintos összeghatárig.{" "}
											<strong>
												Itt nem elvárás, hogy az építkezéshez csok támogatás kapcsolódjon!
											</strong>
										</li>
									</ul>
									<p>
										Az adó-visszatérítés egyetlen alkalommal igényelhető, és csak azonos – 5, vagy
										27%-os – áfatartalmú számlák alapján kérhető a támogatás.
									</p>
								</LandingAccordion>
								<LandingAccordion title="Lakásfelújítási támogatás" parentName="tamogatasok">
									<p>
										Meghatározott felújítási és korszerűsítési munkálatok költségeinek felét,
										maximum 3 millió forintot utólag visszafizet az Állam. A támogatást a legalább
										egy gyermeket várók vagy nevelők – azonos lakóhellyel rendelkező házastársak
										vagy élettársak közösen, együttes igénylőként – igényelhetik. Vagyis
										egyedülállók is igényelhetik, nem feltétele a házasság.
									</p>
									<p>
										<a href="/felujitasi-hitel/?link_type=landing-faq">Tovább a kalkulátorra</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="NHP Zöld Otthon Program" parentName="tamogatasok">
									<p>
										Az NHP Zöld Otthon Program keretében 2021. októbertőlelérhető egy kedvezményes,{" "}
										<strong>fix 2,5 százalékos kamatozású</strong> lakáshitel, ezt szokás zöld
										hitelnek nevezni.
									</p>
									<p>
										A kölcsön kizárólag új építésű lakás vásárlására és építkezésre fordítható. Az
										új lakásnak, háznak pedig <strong>energiatakarékos kell legyen</strong>: az
										épület primer energiaigénye nem haladhatja meg a 90kWh/m2/év értéket, illetve a
										ház energetikai besorolása el kell érje legalább a „BB” kategóriát.
									</p>
									<p>
										A program keretében összességében legfeljebb 200 milliárd forint hitel
										igényelhető. Érdemes gyorsan dönteni a kölcsön felvételéről a szakértők szerint
										ugyanis a keretösszeg akár 2022. nyarára el is fogyhat.
									</p>
									<p>
										<a href="/zold-hitel/?link_type=landing-faq">Zöld lakáshitel részletek</a>
									</p>
								</LandingAccordion>
							</Accordion>
						</Tab>
						<Tab eventKey="gyakorlati-tippek" title="Gyakorlati tippek">
							<Accordion>
								<LandingAccordion
									title="Nem ad elegendő lakáshitelt a bank? Mutatjuk a lehetőségeidet"
									parentName="gyakorlati-tippek"
								>
									<p>
										Amikor hitelt igénylünk, általában meghatározott összegre van szükségünk, aminél
										ha kevesebbet ad a bank, akkor nem tudjuk megvenni a kiszemelt lakást, esetleg
										nem sikerül elvégezni a tervezett felújítást. Segíthet ilyenkor egy adóstárs
										bevonása, amivel növelhető az igazolt jövedelem nagysága, de a futamidő
										meghosszabbítása, vagy akár meglévő hiteleink kiváltása is megoldást jelenthet.{" "}
										<a href="/mediatar/cikk/nem-ad-elegendo-lakashitelt-a-bank-mutatjuk-a-lehetosegeidet/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Sokat spórolhatsz a lakáshitel egyszeri díjain!"
									parentName="gyakorlati-tippek"
								>
									<p>
										Egy átlagosnak számító lakáshitel igénylésének költsége pár tízezer és 200 ezer
										forint egyaránt lehet. A tartozáselismerő okirat elkészítésének költsége, a
										hitelbírálat és a folyósítás díja általában nagyon különböző az egyes bankoknál,
										míg például a közjegyzői okirat elkészítésének díja, a földhivatali ügyintézés
										költsége és az értékbecslés díja független a pénzintézetektől. Jó tudni, hogy a
										bankok sok esetben utólag – a folyósítást követően – visszatérítik a díjak egy
										részét. A díj elengedésének, visszatérítésének azonban többnyire feltétele is
										van, például több bank nyújt jelentős kedvezményeket csak azért, ha a
										Bankmonitoron keresztül igényled a hitelt!{" "}
										<a href="/mediatar/cikk/van-amikor-a-thm-tevutra-vezet-egy-lakashitel-kivalasztasanal/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Van, amikor a THM tévútra vezet egy lakáshitel kiválasztásánál"
									parentName="gyakorlati-tippek"
								>
									<p>
										A teljes hiteldíj-mutató, azaz a THM mutatja meg legpontosabban egy hitel teljes
										éves költségét. Éppen ezért alkalmazzák különböző hitelkonstrukciók
										összehasonlítására, illetve érdemes ez alapján kiválasztani a legkedvezőbb
										kölcsönt. Van azonban néhány élethelyzet, amikor a THM torzít, ezért féltevezető
										lehet. A THM például nem tükrözi egy hitel kamat- és árfolyamkockázatát, de
										például előfordulhat az is, hogy nem a lakáshitel, hanem az ahhoz választott
										számlacsomag miatt lesz magas a mutató.{" "}
										<a href="/mediatar/cikk/van-amikor-a-thm-tevutra-vezet-egy-lakashitel-kivalasztasanal/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Lakáshitel külföldi jövedelemmel"
									parentName="gyakorlati-tippek"
								>
									<p>
										A külföldi jövedelem elfogadását illetően lehetnek különbségek az egyes bankok
										között, így könnyen előfordulhat, hogy míg{" "}
										<strong>az egyiknél kaphatsz hitelt, a másik elutasít</strong>. Ezért erősen
										javallott hitelszakértőhöz fordulni, aki ismeri a pénzintézetek rigolyáit, így
										eleve ott adja be az igénylésed, ahol sikerre számíthatsz.{" "}
										<a href="/mediatar/cikk/hazakoltoznel-kulfoldrol-es-lakast-vennel-idehaza-mutatjuk-kaphatsz-e-hitelt/?link_type=landing-faq">
											További információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Felvehetünk lakáshitelt a párunk bevonása nélkül?"
									parentName="gyakorlati-tippek"
								>
									<p>
										Az egyik leggyakoribb ok, ami miatt egy pár valamelyik tagja egyedül szeretne
										lakáshitelhez jutni az, hogy a másik tag negatív státusszal szerepel a KHR
										adatbázisban. Ez azt jelenti, hogy van olyan tartozása, amelyet nem törlesztett
										kellő megbízhatósággal a bank felé, emiatt pedig a bankok nem hajlandók újabb
										hitelt folyósítani számára. Ilyen helyzetekre nyújthat megoldást az ún.
										„vagyonjogi megállapodás”, melyet a pár tagjai kötnek egymással. Ebben a felek
										felülírhatják a vagyonközösséget és kijelenthetik, hogy a megvenni kívánt
										ingatlan kizárólag az egyik fél tulajdonát képezi majd, ő egyedül lesz felelős a
										hitel törlesztéséért is.{" "}
										<a href="/mediatar/cikk/felvehetunk-lakashitelt-a-parunk-bevonasa-nelkul/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mi lesz a hiteleddel válás esetén?"
									parentName="gyakorlati-tippek"
								>
									<p>
										Sokan nem tudják, hogy míg a gyermekelhelyezést és a vagyonmegosztást illetően a
										bíróság hozhat döntés váláskor, a hitelszerződések sorsáról nem dönthet, ugyanis
										ez a magánszemélyek és a bank (vagy bankok) között jött létre, így a módosítás a
										pénzintézet bevonása nélkül nem lehetséges. Ezért a válást mindenképp jelezni
										kell a bank felé is.{" "}
										<a href="/mediatar/cikk/mi-lesz-a-csok-kal-a-babavaroval-vagy-a-lakashitellel-valas-eseten/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Erre figyelj, ha jelzáloggal terhelt ingatlant vásárolnál hitelből"
									parentName="gyakorlati-tippek"
								>
									<p>
										Teljesen hétköznapi esetnek számít, hogy a kiszemelt ingatlan nem tehermentes,
										hanem korábbról már fűződik hozzá valamiféle követelés. A bankok szempontjából
										ez nagyon fontos tényező, ezért gyakran ezen múlhat, hogy kapunk-e lakáshitelt,
										és ha igen, milyen feltételekkel. Egy banki hitelbírálat során az ingatlanra
										vonatkozó követelések alapvetően két kategóriába sorolhatók:
									</p>
									<ol>
										<li>
											Elfogadható terhek, amelyek nem gátolják meg a hitelszerződés létrejöttét.
										</li>
										<li>
											A bank számára elfogadhatatlan terhek, amelyek fennállásáig szinte biztosan
											nem fogunk hitelhez jutni.
										</li>
									</ol>
									<p>
										<a href="/mediatar/cikk/terhelt-ingatlant-vasarolnal-keszulj-fel-a-buktatokra/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Gondokat okozhat egy építkezés csúszása, ha lakáshitelt is igényeltél!"
									parentName="gyakorlati-tippek"
								>
									<p>
										Az újépítésű, különösen a tervezőasztalon megvásárolt lakások esetében az
										építkezés csúszása már-már természetesnek mondható, aminek oka lehet a
										koronavírus helyzet, a munkaerőhiány, de sok galibát okoz az építőanyagok
										esetenként nehézkes beszerzése is. A kérdés tehát jelenleg nem is az, hogy
										csúszik-e egy építkezés, inkább az, hogy mennyit, illetve a befejezést a vevő ki
										tudja-e várni. Különösen fontos tisztában lenned a csúszás következményeivel
										akkor, ha lakáshitelből vásárolnád meg az újépítésű ingatlant!{" "}
										<a href="/mediatar/cikk/mit-okozhat-a-csuszas-egy-lakashitelnel-ha-csuszik-az-epitkezes/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Erre figyelj, ha telket vásárolnál lakáshitelből"
									parentName="gyakorlati-tippek"
								>
									<p>
										Nem mindegy, hogy a telek a település külterületén vagy belterületén
										helyezkedik-e el, de a művelési ág is befolyásolja, hogy kaphatsz-e rá
										jelzáloghitelt. Előfordul, hogy a telek megnevezése nem megfelelő – gazdasági
										épület, istálló –, ez megint arra utal, hogy a terület nem építési telekként
										funkcionál, hanem valamilyen gazdasági területnek minősül. Az is okozhat
										problémát, ha a telken elbontandó ingatlan áll. Van pénzintézet, amelyik
										ilyenkor nem nyújt lakáshitelt, míg mások levonják a telek forgalmi értékéből a
										felépítmény bontási költségét és ez alapján határozzák meg felvehető
										hitelösszeget.{" "}
										<a href="/mediatar/cikk/mire-kell-figyelnunk-ha-telket-vasarolnank-lakashitelbol/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Bukhatod a csok-ot és a csok hitelt, ha a vételár túl nagy részét fizeted készpénzben!"
									parentName="gyakorlati-tippek"
								>
									<p>
										A csok-nak és a csok hitelnek van egy olyan feltétele, ami komoly problémát
										okozhat: a lakás vételárának maximum 10%-át lehet készpénzben átadni az
										eladónak. Például egy 30 millió Ft-os ingatlan esetében maximum 3 millió
										forintot lehet készpénzben kifizetni. Önmagában ez nem tűnik
										teljesíthetetlennek, azonban sokan csak az adásvételi szerződés megkötése és a
										foglaló megfizetése után szembesülnek a szabállyal, ami megnehezítheti a csok
										igénylését.{" "}
										<a href="/mediatar/cikk/bukhatod-a-csok-ot-es-a-csok-hitelt-is-ha-a-vetelar-tul-nagy-reszet-fizeted-keszpenzben/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Az 5%-os áfa-visszatérítést a csok támogatással együtt kell a pénzintézetnél igényelned"
									parentName="gyakorlati-tippek"
								>
									<p>
										Az adó-visszatérítést minden esetben a kiválasztott pénzintézetnél lehet
										megigényelni, amennyiben pedig csok támogatást is kér a család, akkor a két
										támogatási kérelmet ezzel együtt kell benyújtani. (Az 5%-os áfa-visszatérítés
										feltétele, hogy a család családi otthonteremtési kedvezményt is igényeljen.){" "}
										<a href="/mediatar/cikk/5-buktato-amire-figyelni-kell-az-afa-visszaterites-igenylesekor/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Építésnél a költségvetés 70%-ról azonos áfatartalmú számlát kell bemutatni az áfa-visszatérítéshez"
									parentName="gyakorlati-tippek"
								>
									<p>
										A teljes költségvetés 70%-ról mindenképpen számlát szükséges bemutatni az
										áfa-visszatérítéshez – ez elvárás csok igénylésnél is -, azonban egyáltalán nem
										mindegy a számlák adótartalma. Ha 5%-os számlák adótartalmát igényelnéd vissza,
										akkor a költségvetés 70%-ról 5%-os adótartalmú számlára lesz szükséged. (Ez
										természetesen a 27%-os adó-visszatérítésre is igaz, ebben az esetben 27%-os
										adótartalmú számla szükséges a költségvetés 70 százalékáról.){" "}
										<a href="/mediatar/cikk/5-buktato-amire-figyelni-kell-az-afa-visszaterites-igenylesekor/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="A 27%-os áfa-visszatérítésnél is teljesíteni szükséges a csok alapvető személyi feltételeit"
									parentName="gyakorlati-tippek"
								>
									<p>
										Az önerős építkezésekhez kapcsolódó adó-visszatérítéshez nem szükséges családi
										otthonteremtési kedvezményt igényelni, sőt gyermekre sincs szükség hozzá. Ettől
										függetlenül teljesíteni kell a csok támogatáshoz kapcsolódó alapvető személyi
										feltételeket:
									</p>
									<ul>
										<li>Legalább fél éves Tb-jogviszony</li>
										<li>Büntetlen előélet</li>
										<li>Nem lehet köztartozása az adó-visszatérítést kérelmezőnek</li>
									</ul>
									<p>
										<a href="/mediatar/cikk/5-buktato-amire-figyelni-kell-az-afa-visszaterites-igenylesekor/?link_type=landing-faq">
											Még több információ
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion
									title="Mi ellen véd egy hitelfedezeti biztosítás?"
									parentName="gyakorlati-tippek"
								>
									<p>
										Munkahelyünk elvesztése, egy tartós betegség, esetleg egy személyes tragédia –
										rokkantság vagy haláleset – anyagi helyzetünkre is kihat, így előfordulhat, hogy
										a hitelünket sem vagyunk képesek szerződés szerint fizetni. Ezekben a
										szituációkban anyagi támaszt jelenthet egy hitelfedezeti biztosítás. Fontos
										azonban tisztában lenni azzal, hogy mire és mennyit fizetnek ezek a
										biztosítások!{" "}
										<a href="/mediatar/cikk/mi-ellen-ved-egy-hitelfedezeti-biztositas-lehetosegek-es-buktatok/?link_type=landing-faq">
											További információ
										</a>
									</p>
								</LandingAccordion>
							</Accordion>
						</Tab>
						<Tab eventKey="hogyan-segitunk" title="Hogyan segítünk?">
							<ul className="list-unstyled">
								<li>
									<strong>1.</strong> A magyar banki ajánlatok 95%-ának objektív összehasonlításával
									segítünk
								</li>
								<li>
									<strong>2.</strong> Órákon belül megmondjuk, hogy a meglévő jövedelmed és a
									kiválasztott ingatlan mellett mennyi hitelt, milyen feltételekkel vehetsz fel
								</li>
								<li>
									<strong>3.</strong> Rengeteg olyan kérdésre választ adunk, amit a bankfiókokban
									hiába tennél fel (segítünk megérteni a kockázatokat)
								</li>
								<li>
									<strong>4.</strong> Az ország legjobbjai közé tartozó specializált szakértőkkel
									dolgozunk
								</li>
								<li>
									<strong>5.</strong> Próbáltál már bankfiókot telefonon elérni? Mi hétvégén is
									fogadjuk a hívásodat!
								</li>
								<li>
									<strong>6.</strong> Teljes körű megoldást adunk Neked, minden papírmunkát
									átvállalunk a hitelügyintézéskor
								</li>
								<li>
									<strong>7.</strong> Ha elakad a hiteligényed, azonnal beavatkozunk és megoldjuk a
									problémát
								</li>
								<li>
									<strong>8.</strong> Számos olyan kedvezménnyel rendelkezünk, amit egy bankfiókban
									nem érsz el
								</li>
								<li>
									<strong>9.</strong> Szolgáltatásunk díjmentes Számodra
								</li>
								<li>
									<strong>10.</strong> Ismerjük az apróbetűs részeket is
								</li>
								<li>
									<strong>+1.</strong> Minden ügyfelünk kiemelten fontos számunkra, ezért térnek
									vissza a Bankmonitorhoz az új pénzügyi kérdéseikkel is
								</li>
							</ul>
						</Tab>
						<Tab eventKey="hogyan-kezd-el" title="Hogyan kezd el?">
							<Accordion>
								<LandingAccordion title="Hogyan válasszak lakáshitelt?" parentName="hogyan-kezd-el">
									<p>
										A lakáshitel az egyik legbonyolultabb kölcsön, segítünk megtalálni a számodra
										legkedvezőbb hitelajánlatot.
										<br />
										<a
											href="/hogyan-valasszak-lakashitelt/?link_type=landing-faq"
											className="text-underlined"
										>
											<u>Tudj meg többet</u>
										</a>
									</p>
								</LandingAccordion>
								<LandingAccordion title="Hogyan igényelhetek lakáshitelt?" parentName="hogyan-kezd-el">
									<p>
										Megtudhatod a hiteligénylés folyamatát: a megfelelő lépéseket követve gyorsabban
										kaphatod meg a hitelösszeget.
										<br />
										<a
											href="/lakashitel-igenyles/?link_type=landing-faq"
											className="text-underlined"
										>
											<u>Tudj meg többet</u>
										</a>
									</p>
								</LandingAccordion>
							</Accordion>
						</Tab>
					</Tabs>
				</LandingContainer>
				<LandingContainer size="lg" bgClassName="bg-light">
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
						További <mark className="bg-light text-secondary">hitel- és támogatás</mark> kalkulátorok
					</h2>
					{this.renderCards()}
				</LandingContainer>
				{this.renderVideos()}
				<LandingBottom tags="lakashitel" />
				<StructuredDataSchema schema={this.getSchema()} />
			</>
		);
	}

	private renderCards(): ReactNode {
		const colProps = {
			className: "my-3 align-items-stretch",
			lg: 3,
			md: 4,
			sm: 6,
		};

		return (
			<Row>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Mennyi lakáshitelt <br className="d-sm-block d-none" />
								kaphatok?
							</>
						}
						icon={<FontAwesomeIcon icon={farQuestion} />}
						href="/lakashitel-kalkulator/mennyi-lakashitelt-kaphatok/"
						buttonText="Kalkuláció indítása"
					>
						Tudd meg, hogy jövedelmed és meglévő hiteleid alapján maximálisan mekkora összegű lakáshitelt
						kaphatsz.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Otthonteremtési <br className="d-sm-block d-none" />
								támogatás
							</>
						}
						icon={<FontAwesomeIcon icon={farHouseHeart} />}
						href="/otthonteremtesi-es-otthonfelujitasi-tamogatasok/"
						buttonText="Kalkuláció indítása"
					>
						Tudj meg többet lakásáfa kedvezmény, lakásfelújítási támogatás, CSOK és Babaváró támogatás
						témakörben!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Babaváró <br className="d-sm-block d-none" />
								hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farBabyCarriage} />}
						href="/babavaro-hitel/"
						buttonText="Kalkuláció indítása"
					>
						Számítsd ki hitelkalkulátorunkkal, hogy mennyi Babaváró hitelre vagy jogosult!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Lakáshitel <br className="d-sm-block d-none" />
								kiváltás
							</>
						}
						icon={<FontAwesomeIcon icon={farShuffle} />}
						href="/hitelkivaltas-reszletek/"
						buttonText="Kalkuláció indítása"
					>
						A hitelkiváltás kalkulátorral ellenőrizheted, hogy meglévő lakáshitelednél elérhető-e kedvezőbb
						a piacon.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								CSOK <br className="d-sm-block d-none" />
								támogatás
							</>
						}
						icon={<FontAwesomeIcon icon={farBaby} />}
						href="/csok-feltetelei/"
						buttonText="Kalkuláció indítása"
					>
						Tudd meg, mennyi CSOK támogatást kaphatsz lakásvásárlásra, építésre vagy bővítésre!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Szabad <br className="d-sm-block d-none" />
								felhasználású hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farHouse} />}
						href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/"
						buttonText="Kalkuláció indítása"
					>
						Szabad célú jelzáloghitelek akár 100 millió Ft hitelösszegig. Kalkulálj és találd meg a
						legkedvezőbb ajánlatokat!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Személyi <br className="d-sm-block d-none" />
								kölcsön
							</>
						}
						icon={<FontAwesomeIcon icon={farHandHoldingDollar} />}
						href="/szemelyi-kolcson/"
						buttonText="Kalkuláció indítása"
					>
						14 bank szabad felhasználású, fix kamatú személyi kölcsöneinek összehasonlítása pillanatok
						alatt.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Támogatott <br className="d-sm-block d-none" /> felújítási hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farPaintRoller} />}
						href="/felujitasi-hitel/"
						buttonText="Kalkuláció indítása"
					>
						Akár 6 millió Ft kedvezményes hitel az otthonfelújítási támogatás előfinanszírozására. Kalkulálj
						a részletekért!
					</FeatureBox>
				</Col>
			</Row>
		);
	}

	private renderVideos(): ReactNode {
		return (
			<LandingContainer as="aside">
				<div className="headline">
					<h2 className="h6 text-uppercase">Kapcsolódó videók</h2>
				</div>
				<Row className="justify-content-center">
					<Col lg={3} md={6}>
						<YoutubeVideo
							title="Lakáshitel igénylés - minden, amit tudnod kell 2023-ban"
							videoId="P2wKw6kNfNo"
							description="Lakáshitel igénylés előtt állsz? Szakértőnk végig vezet a folyamaton, hogy elkerüld a buktatókat."
							uploadDate="2023-01-16"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-md-0">
						<YoutubeVideo
							title="Hiteligénylés - Mennyi törlesztőt vállaljunk be?"
							videoId="Y9vOsO1GAd4"
							description="Mennyi törlesztőt vállaljunk be?"
							uploadDate="2023-02-21"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Erre vártál: olcsók lettek a magyar lakások!"
							videoId="hoW2I30Pa3A"
							description="2022 közepén valami megváltozott: elkezdtek csökkeni a lakásárak. De most már olcsó, vagy nem olcsó? Miben mérjük? Forintban, euróban, bitcoinban, aranyban… fakockában? Hogyan vesszük figyelembe az infláció hatását? Új videónkban több szempontból is bemutatjuk milyen erős átárazódás, leértékelődés ment végbe 2022 közepe óta a magyar lakáspiacon. Az ingatlanárak már érdemben csökkentek, egy hatalmas ár visszarendeződés következett be az ingatlanpiaco Felesleges arra várni, hogy jövőre 25%-kal olcsóbb lesz a lakásár (árcédula szintjén). Mindez nem jelenti azt, hogy innen növekedni fognak. Sokkal inkább számíthatunk a közeljövőben 1-2-3%-os lassú lemorzsolódásra, stagnálásra. Az infláció majd teszi a dolgát és reálértéken, vagy kenyérben kifejezve lesz olcsóbb a lakás."
							uploadDate="2022-06-06"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Lakást venni vagy nem venni - miből és hogyan?"
							videoId="2VRuGes1Jv4"
							description="Előző videónkban bemutattuk, hogy masszívan leértékelődtek a lakások (Erre vártál: olcsók lettek a magyar lakások). Ma az ingatlanra vágyok elsődleges dilemmájára fókuszálunk: most vegyek, vagy ha lecsökken a lakáshitel kamata? Miért? - Magas kamat = magas törlesztőrészlet - Nem akarok ennyit fizetni 20 éven keresztül - Inkább megvárom az alacsonyabb kamatokat Helyes ez a megközelítés? Végtelenül egyszerű számításon keresztül bemutatjuk a valós alternatívákat és a tényleges pénzügyi hatásokat. Nem utolsó sorban szót ejtünk a múlt tapasztalatairól is."
							uploadDate="2022-06-07"
						/>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private getSchema(): FAQSchema {
		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: [
				{
					"@type": "Question",
					name: "Melyek a lakáshitel legfontosabb feltételei?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Az igényléshez szükséges egy megfelelő értékű ingatlanfedezet bevonása. A fedezet lehet maga a megvásárolni kívánt ingatlan, de akár más ingatlan is szóba jöhet. Ezek kívül szükséges igazolnia az igénylőnek azt is, hogy van jövedelme, valamint azt, hogy legalább 3 hónapja dolgozik egyazon munkahelyen, és már nem próbaidős.",
					},
				},
				{
					"@type": "Question",
					name: "Mitől függ, hogy mennyi lakáshitelt kaphatok?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A felvehető lakáshitel összege függ a megvásárolni kívánt ingatlan értékétől, valamint az igénylő által igazolt jövedelem nagyságától. A hitel összege maximum a megvásárolni kívánt ingatlan vételárának 80%-a lehet, míg a havi törlesztő nem haladhatja meg a nettó jövedelem 50-60%-át.",
					},
				},
				{
					"@type": "Question",
					name: "Melyik bank kínálja a legolcsóbb lakáshitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A bankok ajánlatai folyamatosan változnak, így egyáltalán nem biztos, hogy ugyanaz a bank kínálja a legkedvezőbb feltételeket most, mint akár néhány hete. Éppen ezért fontos összehasonlítani a bankok aktuális hitelajánlatait egy naponta frissülő hitelkalkulátorral.",
					},
				},
				{
					"@type": "Question",
					name: "Önerő nélkül is kaphatok lakáshitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Egy ingatlan megvásárlásakor általában a vételár 20%-a a minimálisan elvárt önerő, míg a fennmaradó részt hitelezi meg a bank. További ingatlanfedezet bevonásával azonban akár önerő nélkül is megoldható a hitellel történő vásárlás. Ilyenkor a fedezetként bevont ingatlanok összértékének 80%-áig hitelezhetnek a bankok.",
					},
				},
				{
					"@type": "Question",
					name: "Önerőként elfogadják a CSOK-ot a bankok?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A meglévő gyermek után igényelt CSOK támogatást – családi otthonteremtési kedvezmény – a bankok szinte minden esetben elfogadják önerőként, ám a vállalt gyermekekre igényelt támogatás – megelőlegező CSOK – esetében már más a helyzet. Ez utóbbit a bankoknak csak egy része fogadja el önerőként.",
					},
				},
				{
					"@type": "Question",
					name: "Hogyan kaphatok hitelt építkezéshez?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Az építési hitel egyik sajátossága, hogy a bank azt nem egy összegben utalja ki, hanem szakaszosan, az építkezés készültségi fokának megfelelően folyósítja. Amint egy építési fázis befejeződik, a banki értékbecslő helyszíni szemlét végez, és ez alapján utalja a bank a következő részösszeget. A másik egyediség, hogy a maximálisan felvehető hitelösszeget nem csak a fedezetként ajánlott telek értéke határozza meg, hanem a rá építeni kívánt felépítmény várható majdani értéke. Hiszen a banki jelzálogot a kész házra fogják bejegyezni.",
					},
				},
				{
					"@type": "Question",
					name: "Mi az a Minősített Fogyasztóbarát Lakáshitel?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A Minősített Fogyasztóbarát Lakáshitelek esetében a bankoknak számos feltételnek kell megfelelniük, emiatt sokszor kedvezőbbek a piaci lakáshiteleknél. A banki kamatfelár maximum 3,5% lehet, a leghosszabb futamidő 30 év, a kamatperiódus nem lehet rövidebb 5 évnél, a folyósítási díj pedig nem haladhatja meg a hitelösszeg 0,75%-át, de legfeljebb 150 ezer forintot. Ezen kívül az elő- és végtörlesztési díj nem lehet magasabb az elő-/végtörlesztett összeg 1%-ánál, de a lakástakarék-pénztári betétből végrehajtott elő/végtörlesztés például díjmentes.",
					},
				},
				{
					"@type": "Question",
					name: "Melyik lakáshitel a legbiztonságosabb?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Legbiztonságosabbak a futamidő végéig rögzített kamatú jelzáloghitelek, hiszen ezeknél a kamat és a havi törlesztő végig változatlan marad.",
					},
				},
				{
					"@type": "Question",
					name: "Mikor emelkedhet a lakáshitel havi törlesztője?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A lakáshitelek havi törlesztője abban az esetben emelkedhet, ha a hitel referenciamutatója is felfelé mozdul el. A referenciamutató a változó kamatú hiteleknél általában a BUBOR, míg a hosszú kamatperiódusú hiteleknél a BIRS. Ezek a mutatók azt jelzik, hogy a bankok adott feltételekkel milyen kamat mellett adnának egymásnak hitelt, vagyis ez tekinthető a kihelyezett pénz „beszerzési árának”.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyivel emelkedhet a lakáshitel kamat?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Mivel a lakáshitelek futamideje akár 30 év is lehet, nem tudható előre, hogy milyen kamatemelkedés várható. Éppen ezért érdemes szakértő segítségét kérni, aki kiszámolja, hogy egy esetleges kamatemelkedés pontosan hogyan befolyásolná a felvenni kívánt hitel havi fizetnivalóit.",
					},
				},
			],
		};
	}
}
